import { useState, useCallback, useRef, useEffect } from 'react';
import { apolloService, mondayService } from '@/services/api';
import { generateId, getStoredData, STORAGE_KEYS, createDebouncedCloudStorage, logger } from '@/utils';
import type { Lead, Visitor, AsyncState } from '@/types';

const debouncedLeadsStorage = createDebouncedCloudStorage<Lead[]>(STORAGE_KEYS.LEADS, 'leads-local', 3000);

interface UseLeadsConfig {
  useMockData: boolean;
  initialData?: Lead[];
}

export function useLeads({ useMockData, initialData }: UseLeadsConfig) {
  const [leads, setLeads] = useState<AsyncState<Lead[]>>(() => {
    if (initialData && initialData.length > 0) {
      return { data: initialData, loading: false, error: null };
    }
    const stored = getStoredData<Lead[]>(STORAGE_KEYS.LEADS, []);
    return {
      data: stored.length > 0 ? stored : [],
      loading: false,
      error: null,
    };
  });

  // Ref to always hold latest leads — avoids stale closures in async operations
  const leadsRef = useRef(leads);
  leadsRef.current = leads;

  const [selectedLeads, setSelectedLeads] = useState<string[]>([]);
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [enrichingLeads, setEnrichingLeads] = useState<Set<string>>(new Set());
  const [syncingContacts, setSyncingContacts] = useState(false);
  const [syncingLeadsToMonday, setSyncingLeadsToMonday] = useState(false);

  const fetched = useRef(false);

  // Persist leads to localStorage + DynamoDB when they change
  useEffect(() => {
    debouncedLeadsStorage.write(leads.data);
  }, [leads.data]);

  // Load from DynamoDB on mount (if localStorage is empty)
  useEffect(() => {
    if (leads.data.length === 0 && !leads.loading) {
      debouncedLeadsStorage.loadFromCloud().then(cloudData => {
        if (cloudData && cloudData.length > 0) {
          setLeads({ data: cloudData, loading: false, error: null });
        }
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchLeads = useCallback(async () => {
    setLeads((prev) => ({ ...prev, loading: prev.data.length === 0, error: null }));

    try {
      const data = await mondayService.getLeads();
      setLeads({ data, loading: false, error: null });
    } catch (error) {
      setLeads((prev) => ({
        ...prev,
        loading: false,
        error: (error as Error).message,
      }));
    }
  }, []);

  const toggleLeadSelection = useCallback((id: string) => {
    setSelectedLeads((prev) =>
      prev.includes(id) ? prev.filter((l) => l !== id) : [...prev, id]
    );
  }, []);

  const enrichLead = useCallback(
    async (lead: Lead, customDomain?: string) => {
      if (lead.apolloEnriched) {
        logger.log(`[useLeads] Lead ${lead.company} already enriched, skipping`);
        return;
      }

      const domain = customDomain ??
        lead.contactEmail?.split('@')[1] ??
        `${lead.company.toLowerCase().replace(/\s+/g, '')}.com`;

      logger.log(`[useLeads] Enriching ${lead.company} using domain: ${domain}`);

      const cached = apolloService.getCachedEnrichment(domain);
      if (cached) {
        logger.log(`[useLeads] Using cached enrichment for ${lead.company}`);
        setLeads((prev) => ({
          ...prev,
          data: prev.data.map((l) =>
            l.id === lead.id
              ? { ...l, apolloEnriched: true, apolloData: cached }
              : l
          ),
        }));
        return;
      }

      setEnrichingLeads((prev) => new Set(prev).add(lead.id));

      try {
        const enrichment = await apolloService.enrichCompany(domain, {
          companyName: lead.company,
        });

        if (enrichment) {
          setLeads((prev) => ({
            ...prev,
            data: prev.data.map((l) =>
              l.id === lead.id
                ? { ...l, apolloEnriched: true, apolloData: enrichment }
                : l
            ),
          }));

          if (!useMockData) {
            await mondayService.updateLead(lead.id, { apolloEnriched: true });
          }
        }
      } catch (error) {
        logger.simpleError(`[useLeads] Enrichment failed for ${lead.company}:`, error);
      } finally {
        setEnrichingLeads((prev) => {
          const next = new Set(prev);
          next.delete(lead.id);
          return next;
        });
      }
    },
    [useMockData]
  );

  // Helper function for parallel enrichment with batched state updates
  const enrichLeadsParallel = useCallback(
    async (leadsToEnrich: Lead[], onProgress?: (completed: number, total: number) => void) => {
      const BATCH_SIZE = 5;
      const results: Map<string, { enriched: boolean; data?: Lead['apolloData'] }> = new Map();
      let completed = 0;

      // Step 1: Process cached enrichments first (instant, no API calls)
      const needsApiCall: Lead[] = [];
      for (const lead of leadsToEnrich) {
        const domain =
          lead.contactEmail?.split('@')[1] ??
          `${lead.company.toLowerCase().replace(/\s+/g, '')}.com`;

        const cached = apolloService.getCachedEnrichment(domain);
        if (cached) {
          results.set(lead.id, { enriched: true, data: cached });
          completed++;
        } else {
          needsApiCall.push(lead);
        }
      }

      if (onProgress && completed > 0) {
        onProgress(completed, leadsToEnrich.length);
      }

      // Step 2: Process API calls in parallel batches
      for (let i = 0; i < needsApiCall.length; i += BATCH_SIZE) {
        const batch = needsApiCall.slice(i, i + BATCH_SIZE);

        const batchPromises = batch.map(async (lead) => {
          const domain =
            lead.contactEmail?.split('@')[1] ??
            `${lead.company.toLowerCase().replace(/\s+/g, '')}.com`;

          try {
            const enrichment = await apolloService.enrichCompany(domain, {
              companyName: lead.company,
            });
            return { leadId: lead.id, enriched: !!enrichment, data: enrichment || undefined };
          } catch {
            return { leadId: lead.id, enriched: false, data: undefined };
          }
        });

        const batchResults = await Promise.all(batchPromises);

        for (const result of batchResults) {
          results.set(result.leadId, { enriched: result.enriched, data: result.data });
          completed++;
        }

        if (onProgress) {
          onProgress(completed, leadsToEnrich.length);
        }
      }

      return results;
    },
    []
  );

  const bulkEnrichLeads = useCallback(
    async (leadIds: string[], onProgress?: (completed: number, total: number) => void) => {
      const leadsToEnrich = leads.data.filter(
        (lead) => leadIds.includes(lead.id) && !lead.apolloEnriched
      );

      if (leadsToEnrich.length === 0) {
        return { enriched: 0, skipped: 0 };
      }

      setEnrichingLeads((prev) => {
        const next = new Set(prev);
        leadsToEnrich.forEach((l) => next.add(l.id));
        return next;
      });

      try {
        const results = await enrichLeadsParallel(leadsToEnrich, onProgress);

        let enriched = 0;
        let skipped = 0;
        results.forEach((result) => {
          if (result.enriched && result.data) {
            enriched++;
          } else {
            skipped++;
          }
        });

        setLeads((prev) => ({
          ...prev,
          data: prev.data.map((lead) => {
            const result = results.get(lead.id);
            if (result?.enriched && result.data) {
              return { ...lead, apolloEnriched: true, apolloData: result.data };
            }
            return lead;
          }),
        }));

        setEnrichingLeads((prev) => {
          const next = new Set(prev);
          leadsToEnrich.forEach((l) => next.delete(l.id));
          return next;
        });

        return { enriched, skipped };
      } catch (error) {
        logger.simpleError('[useLeads] Bulk enrichment failed:', error);
        setEnrichingLeads((prev) => {
          const next = new Set(prev);
          leadsToEnrich.forEach((l) => next.delete(l.id));
          return next;
        });
        return { enriched: 0, skipped: leadsToEnrich.length };
      }
    },
    [leads.data, enrichLeadsParallel]
  );

  const syncContactsToLeads = useCallback(
    async (visitor: Visitor, contactIndices: number[]) => {
      setSyncingContacts(true);

      try {
        const leadsToCreate: Omit<Lead, 'id' | 'createdAt' | 'lastActivity'>[] = [];

        for (const idx of contactIndices) {
          const contact = visitor.suggestedContacts[idx];
          if (!contact) continue;

          const existingLead = leadsRef.current.data.find(
            (l) => l.contactEmail.toLowerCase() === contact.email.toLowerCase()
          );

          if (existingLead) {
            logger.log(`[useLeads] Contact ${contact.email} already exists, skipping`);
            continue;
          }

          leadsToCreate.push({
            company: visitor.company,
            contactName: contact.name,
            contactTitle: contact.title,
            contactEmail: contact.email,
            contactPhone: '',
            stage: 'New',
            value: visitor.icpScore > 85 ? 150000 : visitor.icpScore > 70 ? 100000 : 75000,
            source: 'Apollo - Contact Sync',
            owner: 'Unassigned',
            nextAction: 'Initial outreach',
            nextActionDate: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000)
              .toISOString()
              .split('T')[0] ?? '',
            notes: `Synced from Apollo. Company visited ${visitor.visits} times. ICP Score: ${visitor.icpScore}. Intent: ${visitor.intent}.`,
            apolloEnriched: true,
            apolloData: {
              industry: visitor.industry,
              size: visitor.size,
              location: visitor.location,
              website: `${visitor.company.toLowerCase().replace(/\s+/g, '')}.com`,
              linkedin: `linkedin.com/company/${visitor.company.toLowerCase().replace(/\s+/g, '-')}`,
              founded: 1990 + Math.floor(Math.random() * 30),
              revenue:
                visitor.size === '10,000+'
                  ? '$1B+'
                  : visitor.size?.includes('5,000')
                    ? '$500M+'
                    : '$100M+',
              technologies: ['AWS', 'Salesforce', 'Microsoft 365'],
              recentNews: 'Contact synced from Apollo website visitor data',
              additionalContacts: visitor.suggestedContacts.filter(
                (c) => c.email !== contact.email
              ),
            },
          });
        }

        if (leadsToCreate.length === 0) {
          logger.log('[useLeads] No new contacts to sync');
          return { created: 0, skipped: contactIndices.length };
        }

        if (useMockData) {
          const newLeads: Lead[] = leadsToCreate.map((lead, i) => ({
            ...lead,
            id: `L${String(leadsRef.current.data.length + i + 1).padStart(3, '0')}`,
            createdAt: new Date().toISOString().split('T')[0] ?? '',
            lastActivity: 'Just now',
          }));

          setLeads((prev) => ({
            ...prev,
            data: [...prev.data, ...newLeads],
          }));

          return { created: newLeads.length, skipped: contactIndices.length - newLeads.length };
        } else {
          const { created, skipped } = await mondayService.batchCreateLeads(leadsToCreate, {
            skipDuplicates: true,
          });

          await fetchLeads();

          return { created: created.length, skipped: skipped.length };
        }
      } finally {
        setSyncingContacts(false);
      }
    },
    [useMockData, fetchLeads]
  );

  const importLeads = useCallback(
    async (
      newLeads: Omit<Lead, 'id' | 'apolloEnriched' | 'apolloData'>[],
      listIdOrOptions?: string | { markAsEnriched?: boolean },
      options?: { markAsEnriched?: boolean }
    ): Promise<{ created: number; skipped: number; createdLeads?: Lead[] }> => {
      let listId: string | undefined;
      let markAsEnriched = false;

      logger.log('[importLeads] Called with:', {
        leadsCount: newLeads.length,
        listIdOrOptions,
        options,
      });

      if (typeof listIdOrOptions === 'string') {
        listId = listIdOrOptions;
        markAsEnriched = options?.markAsEnriched ?? false;
      } else if (listIdOrOptions) {
        markAsEnriched = listIdOrOptions.markAsEnriched ?? false;
      }

      logger.log('[importLeads] Parsed:', { listId, markAsEnriched });

      if (newLeads.length === 0) {
        return { created: 0, skipped: 0 };
      }

      const existingEmails = new Set(
        leads.data
          .filter((l) => l.contactEmail)
          .map((l) => l.contactEmail.toLowerCase())
      );
      const uniqueLeads = newLeads.filter((lead) => {
        if (!lead.contactEmail) return true;
        return !existingEmails.has(lead.contactEmail.toLowerCase());
      });

      const skipped = newLeads.length - uniqueLeads.length;

      if (uniqueLeads.length === 0) {
        return { created: 0, skipped };
      }

      const createdLeads: Lead[] = uniqueLeads.map((lead) => ({
        ...lead,
        id: generateId('lead'),
        apolloEnriched: markAsEnriched,
        apolloData: undefined,
        syncedToMonday: false,
      }));

      setLeads((prev) => ({
        ...prev,
        data: [...prev.data, ...createdLeads],
      }));

      return { created: createdLeads.length, skipped, createdLeads };
    },
    [leads.data]
  );

  const markLeadEnriched = useCallback((leadId: string, enriched: boolean) => {
    setLeads((prev) => ({
      ...prev,
      data: prev.data.map((lead) =>
        lead.id === leadId
          ? { ...lead, apolloEnriched: enriched }
          : lead
      ),
    }));
  }, []);

  const syncEnrichedLeadsToMonday = useCallback(
    async (leadIds: string[], options?: { listType?: 'company' | 'contact'; listName?: string }): Promise<{
      updated: number;
      created: number;
      failed: number;
      errors?: string[];
    }> => {
      const leadsToSync = leads.data.filter(
        (lead) => leadIds.includes(lead.id) && lead.apolloEnriched
      );

      if (leadsToSync.length === 0) {
        return { updated: 0, created: 0, failed: 0 };
      }

      setSyncingLeadsToMonday(true);

      logger.log('[useLeads] syncEnrichedLeadsToMonday called with options:', {
        listType: options?.listType,
        listName: options?.listName,
        leadCount: leadsToSync.length,
        isContactList: options?.listType === 'contact',
        isCompanyList: options?.listType === 'company',
      });

      try {
        const today = new Date().toISOString();

        // If syncing a contact list, use the contact sync method
        if (options?.listType === 'contact') {
          logger.log(`[useLeads] CONTACT LIST PATH: Syncing ${leadsToSync.length} contacts to Monday.com Contacts board...`);

          const contactsToSync = leadsToSync.map((lead) => {
            const nameParts = (lead.contactName || '').split(' ');
            const firstName = nameParts[0] || '';
            const lastName = nameParts.slice(1).join(' ') || '';

            return {
              firstName,
              lastName,
              name: lead.contactName,
              email: lead.contactEmail,
              phone: lead.contactPhone || lead.apolloData?.phone,
              title: lead.contactTitle,
              company: lead.company,
              seniority: lead.seniority,
              departments: lead.departments,
              owner: lead.owner,
              website: lead.apolloData?.website,
              linkedinUrl: lead.linkedinUrl,
              city: lead.apolloData?.city || lead.city,
              state: lead.apolloData?.state || lead.state,
              country: lead.apolloData?.country || lead.country,
              industry: lead.apolloData?.industry || lead.industry,
              employeeCount: lead.apolloData?.size,
              technologies: lead.apolloData?.technologies?.join(', '),
            };
          });

          if (useMockData) {
            await new Promise((resolve) => setTimeout(resolve, 2000));

            setLeads((prev) => ({
              ...prev,
              data: prev.data.map((lead) =>
                leadsToSync.some((l) => l.id === lead.id)
                  ? { ...lead, syncedToMonday: true, syncedToMondayAt: today }
                  : lead
              ),
            }));

            return { updated: Math.floor(contactsToSync.length / 2), created: Math.ceil(contactsToSync.length / 2), failed: 0 };
          }

          const result = await mondayService.syncContactsToMonday(contactsToSync, { listName: options?.listName });

          if (result.created > 0 || result.updated > 0) {
            setLeads((prev) => ({
              ...prev,
              data: prev.data.map((lead) =>
                leadsToSync.some((l) => l.id === lead.id)
                  ? { ...lead, syncedToMonday: true, syncedToMondayAt: today }
                  : lead
              ),
            }));
          }

          return result;
        }

        // Handle company lists - sync to Accounts board
        if (options?.listType === 'company') {
          logger.log('[useLeads] COMPANY LIST PATH: Syncing to Accounts board');

          const companiesToSync = leadsToSync.map((lead) => ({
            company: lead.company,
            website: lead.apolloData?.website ?? lead.website,
            industry: lead.apolloData?.industry,
            size: lead.apolloData?.size,
            location: lead.apolloData?.location,
            linkedin: lead.apolloData?.linkedin,
            technologies: lead.apolloData?.technologies,
          }));

          if (useMockData) {
            await new Promise((resolve) => setTimeout(resolve, 2000));

            setLeads((prev) => ({
              ...prev,
              data: prev.data.map((lead) =>
                leadsToSync.some((l) => l.id === lead.id)
                  ? { ...lead, syncedToMonday: true, syncedToMondayAt: today }
                  : lead
              ),
            }));

            return { updated: Math.floor(companiesToSync.length / 2), created: Math.ceil(companiesToSync.length / 2), failed: 0 };
          }

          const result = await mondayService.syncCompaniesToMonday(companiesToSync, { listName: options?.listName });

          if (result.created > 0 || result.updated > 0) {
            setLeads((prev) => ({
              ...prev,
              data: prev.data.map((lead) =>
                leadsToSync.some((l) => l.id === lead.id)
                  ? { ...lead, syncedToMonday: true, syncedToMondayAt: today }
                  : lead
              ),
            }));
          }

          return result;
        }

        // Standard lead sync logic (fallback)
        logger.simpleWarn('[useLeads] FALLBACK PATH: Syncing to Leads board (no listType matched)', {
          receivedListType: options?.listType,
          expectedTypes: ['contact', 'company'],
        });
        let updated = 0;
        let created = 0;
        let failed = 0;
        const errors: string[] = [];

        const existingLeads = leadsToSync.filter((l) => !l.id.startsWith('lead-'));
        const newLeads = leadsToSync.filter((l) => l.id.startsWith('lead-'));

        if (useMockData) {
          await new Promise((resolve) => setTimeout(resolve, 2000));

          updated = existingLeads.length;

          for (const lead of newLeads) {
            logger.log(`[useLeads] Mock: Creating account and lead for ${lead.company}`);
            created++;
          }

          setLeads((prev) => ({
            ...prev,
            data: prev.data.map((lead) =>
              leadsToSync.some((l) => l.id === lead.id)
                ? {
                    ...lead,
                    syncedToMonday: true,
                    syncedToMondayAt: today,
                    notes: lead.notes
                      ? `${lead.notes}\n\n[Auto-synced by Marketing Intelligence Hub on ${today.split('T')[0]}]`
                      : `[Auto-created by Marketing Intelligence Hub on ${today.split('T')[0]}]`,
                  }
                : lead
            ),
          }));
        } else {
          for (const lead of existingLeads) {
            try {
              await mondayService.updateLead(lead.id, {
                contactPhone: lead.apolloData?.phone ?? lead.contactPhone,
              });

              if (lead.apolloData) {
                try {
                  await mondayService.createEnrichmentUpdate(lead.id, {
                    industry: lead.apolloData.industry,
                    size: lead.apolloData.size,
                    location: lead.apolloData.location,
                    website: lead.apolloData.website,
                    linkedin: lead.apolloData.linkedin,
                    phone: lead.apolloData.phone,
                  });
                } catch (updateErr) {
                  logger.simpleWarn(`[useLeads] Could not create enrichment update for ${lead.company}:`, updateErr);
                }

                try {
                  await mondayService.updateLinkedAccount(
                    lead.id,
                    {
                      industry: lead.apolloData.industry,
                      size: lead.apolloData.size,
                      location: lead.apolloData.location,
                      website: lead.apolloData.website,
                      linkedin: lead.apolloData.linkedin,
                      technologies: lead.apolloData.technologies,
                    },
                    {
                      company: lead.company,
                      revenue: lead.apolloData.revenue,
                      value: lead.value,
                      stage: lead.stage,
                      nextAction: lead.nextAction,
                    }
                  );
                } catch (accountErr) {
                  logger.simpleWarn(`[useLeads] Could not update linked account for ${lead.company}:`, accountErr);
                }
              }
              updated++;
            } catch (err) {
              logger.simpleError(`[useLeads] Failed to update lead ${lead.id}:`, err);
              failed++;
              errors.push(`Failed to update ${lead.company}: ${err instanceof Error ? err.message : 'Unknown error'}`);
            }
          }

          for (const lead of newLeads) {
            try {
              const newId = await mondayService.createLead({
                company: lead.company,
                contactName: lead.contactName,
                contactTitle: lead.contactTitle,
                contactEmail: lead.contactEmail,
                contactPhone: lead.apolloData?.phone ?? lead.contactPhone,
                stage: 'Identified',
                value: lead.value,
                source: lead.source || 'Marketing Intelligence Hub',
                owner: lead.owner,
                nextAction: lead.nextAction || 'Initial outreach',
                nextActionDate: lead.nextActionDate || (new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0] ?? ''),
                notes: '',
                apolloEnriched: true,
              }, { listName: options?.listName });

              if (lead.apolloData) {
                try {
                  await mondayService.createEnrichmentUpdate(newId, {
                    industry: lead.apolloData.industry,
                    size: lead.apolloData.size,
                    location: lead.apolloData.location,
                    website: lead.apolloData.website,
                    linkedin: lead.apolloData.linkedin,
                    phone: lead.apolloData.phone,
                  });
                } catch (updateErr) {
                  logger.simpleWarn(`[useLeads] Could not create enrichment update for new lead ${lead.company}:`, updateErr);
                }

                try {
                  await mondayService.updateLinkedAccount(
                    newId,
                    {
                      industry: lead.apolloData.industry,
                      size: lead.apolloData.size,
                      location: lead.apolloData.location,
                      website: lead.apolloData.website,
                      linkedin: lead.apolloData.linkedin,
                      technologies: lead.apolloData.technologies,
                    },
                    {
                      company: lead.company,
                      revenue: lead.apolloData.revenue,
                      value: lead.value,
                      stage: 'Identified',
                      nextAction: lead.nextAction || 'Initial outreach',
                    }
                  );
                } catch (accountErr) {
                  logger.simpleWarn(`[useLeads] Could not update linked account for new lead ${lead.company}:`, accountErr);
                }
              }

              setLeads((prev) => ({
                ...prev,
                data: prev.data.map((l) =>
                  l.id === lead.id
                    ? { ...l, id: newId, syncedToMonday: true, syncedToMondayAt: today }
                    : l
                ),
              }));
              created++;
            } catch (err) {
              logger.simpleError(`[useLeads] Failed to create lead ${lead.company}:`, err);
              failed++;
              errors.push(`Failed to create ${lead.company}: ${err instanceof Error ? err.message : 'Unknown error'}`);
            }
          }

          if (updated > 0) {
            setLeads((prev) => ({
              ...prev,
              data: prev.data.map((lead) =>
                existingLeads.some((l) => l.id === lead.id)
                  ? { ...lead, syncedToMonday: true, syncedToMondayAt: today }
                  : lead
              ),
            }));
          }
        }

        return { updated, created, failed, errors: errors.length > 0 ? errors : undefined };
      } finally {
        setSyncingLeadsToMonday(false);
      }
    },
    [leads.data, useMockData]
  );

  return {
    // Data
    leads: leads.data,
    leadsLoading: leads.loading,
    leadsError: leads.error,

    // Selection
    selectedLeads,
    selectedLead,
    setSelectedLead,

    // Action States
    enrichingLeads,
    syncingContacts,
    syncingLeadsToMonday,

    // Actions
    fetchLeads,
    toggleLeadSelection,
    enrichLead,
    enrichLeadsParallel,
    bulkEnrichLeads,
    syncContactsToLeads,
    importLeads,
    markLeadEnriched,
    syncEnrichedLeadsToMonday,

    // Internal (for orchestrator / cross-domain hooks)
    setLeads,
    leadsRef,
    fetched,
    setSelectedLeads,
  };
}

export type UseLeadsReturn = ReturnType<typeof useLeads>;
